/**
 * /prompt-q · M05 Prompt 管理（S3 Day12）
 */
import { Link, createFileRoute, useRouterState } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { M05Shell, type M05View } from "@/components/m05/m05-shell";
import { PageHeader, type PageHelp } from "@/components/shared/page-help";

export const Route = createFileRoute("/prompt-q")({
	component: PromptQRoute,
});

type PromptView = Exclude<M05View, "runtime"> | "migrated";

const PROMPT_VIEW_COPY: Record<
	PromptView,
	{
		title: string;
		description: string;
		help: PageHelp;
	}
> = {
	management: {
		title: "提示词管理",
		description: "维护主回复提示词、证据区模板、版本和灰度比例。",
		help: {
			title: "提示词管理说明",
			description: "这里维护会影响最终回复的提示词资产和版本配置。",
			sections: [
				{
					title: "这里做什么",
					items: [
						"维护主 Agent 基准提示词和证据区模板。",
						"查看版本、tokens、变量插槽和商户灰度比例。",
						"保存前先确认变更原因，发布后再到验证页和评测运营看效果。",
					],
				},
				{
					title: "不要误读",
					items: [
						"这里是配置管理，不代表改完就已经通过效果验证。",
						"场景话术不应全部堆进主 Agent，应优先由方案、工具、知识和证据注入承接。",
					],
				},
			],
		},
	},
	sandbox: {
		title: "沙盒与发布前评测",
		description: "用模拟问题检查提示词、证据区和回复生成边界。",
		help: {
			title: "沙盒与发布前评测说明",
			description: "这里用于发布前验证，不负责长期维护提示词正文。",
			sections: [
				{
					title: "这里做什么",
					items: [
						"用模拟问题预览证据区、主提示词和阶段输出。",
						"查看哪些资产真实影响回复，哪些还只是合同或待开发位。",
						"从单环节测试进入链路测试台，再用评测运营确认一组样本是否变好。",
					],
				},
				{
					title: "验收边界",
					items: [
						"沙盒预览不等于真实客户回复已发布。",
						"提示词单环节测试复用运行事件，不代表完整自主工具循环已上线。",
					],
				},
			],
		},
	},
	migrated: {
		title: "运行参数入口已迁移",
		description: "M05 只保留 Prompt 资产管理和发布前预览；运行配置和写动作审计已迁到责任页。",
		help: {
			title: "迁移说明",
			description: "P11 页面职责已拆清，M05 不再承载运行配置和写动作确认主入口。",
			sections: [
				{
					title: "去哪里维护",
					items: [
						"P5 接入状态维护 SDK loop、max steps、quality mode 和 write mode。",
						"M04 工具与动作查看 write action audit，并确认或拒绝 pending 写动作。",
					],
				},
				{
					title: "M05 继续负责",
					items: [
						"维护主 Agent 提示词、证据区模板、版本和商户灰度。",
						"用沙盒做发布前预览，但不宣称等同于真实客户回复。",
					],
				},
			],
		},
	},
};

function PromptQRoute() {
	const view = getPromptView(useRouteHash());
	const copy = PROMPT_VIEW_COPY[view];

	return (
		<div
			id={
				view === "management"
					? "prompt-management"
					: view === "migrated"
						? "runtime-settings"
						: "prompt-sandbox"
			}
			className="scroll-mt-6 space-y-4"
		>
			<PageHeader title={copy.title} description={copy.description} help={copy.help} />
			{view === "migrated" ? <M05RuntimeMigration /> : <M05Shell view={view} />}
		</div>
	);
}

function useRouteHash() {
	const routeHash = useRouterState({ select: (state) => state.location.hash });
	if (routeHash) return routeHash;
	if (typeof window === "undefined") return "";
	return window.location.hash;
}

function getPromptView(hash: string): PromptView {
	const normalized = hash.replace(/^#/, "");
	if (
		normalized === "prompt-sandbox" ||
		normalized === "prompt-single-stage-contract" ||
		normalized === "main-agent-tool-mode" ||
		normalized === "prompt-asset-change-audit"
	) {
		return "sandbox";
	}
	if (normalized === "runtime-settings" || normalized === "write-actions") {
		return "migrated";
	}
	return "management";
}

function M05RuntimeMigration() {
	return (
		<section
			className="grid gap-4 rounded-md border border-surface-border bg-surface-card p-4 lg:grid-cols-[minmax(0,1fr)_320px]"
			data-testid="m05-runtime-migration"
		>
			<div className="space-y-3">
				<h3 className="text-base font-semibold text-text-primary">Prompt 页面已回归资产管理</h3>
				<p className="max-w-3xl text-sm leading-6 text-text-secondary">
					运行配置已迁移到 P5 接入状态；写动作审计已迁移到 M04 工具与动作。这里不再展示 SDK
					loop、write mode 或 pending 写动作队列，避免把 Prompt 维护和真实运行治理混在一起。
				</p>
				<div className="grid gap-2 sm:grid-cols-2">
					<MigrationLink to="/runtime-closure" hash="agent-runtime-config" label="去 P5 接入状态" />
					<MigrationLink to="/skill-tool" hash="write-actions" label="去 M04 写动作审计" />
				</div>
			</div>
			<div className="rounded-md border border-surface-border bg-surface-background p-3 text-sm leading-6 text-text-secondary">
				<div className="font-semibold text-text-primary">M05 当前职责</div>
				<p className="mt-2">
					维护主 Agent prompt、d' 证据区模板、版本和灰度；沙盒只做发布前预览，真实运行效果回到
					P2/M01 复盘。
				</p>
			</div>
		</section>
	);
}

function MigrationLink({
	to,
	hash,
	label,
}: {
	to: "/runtime-closure" | "/skill-tool";
	hash: string;
	label: string;
}) {
	return (
		<Link
			to={to}
			hash={hash}
			className="inline-flex items-center justify-between rounded-md border border-surface-border bg-surface-background px-3 py-2 text-sm font-medium text-text-primary hover:bg-surface-subtle"
		>
			{label}
			<ArrowRight className="h-3.5 w-3.5 text-text-muted" aria-hidden="true" />
		</Link>
	);
}
